import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { userSaga } from './saga';
import {
  IAccessRightsNumbers,
  IGetUserDetailsPayload,
  IGetUserListPayload,
  ISubmitAddUserFormPayload,
  ISubmitAddUserToCompanyPayload,
  ISubmitRemoveUserFromEnvPayload,
  IUserCompaniesListItem,
  IUserDetails,
  IUserListItem,
  UserState,
} from './types';

export const initialState: UserState = {
  userList: {
    loading: false,
    list: [],
  },
  userDetails: {
    loading: false,
    user: null,
    list: [],
  },
  addUserForm: {
    isOpen: false,
    loading: false,
    type: 'add',
    userEmail: '',
  },
  addUserCompanyForm: {
    isOpen: false,
    loading: false,
    type: 'add',
  },
};

const slice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    submitRemoveUserFromEnv(
      state,
      action: PayloadAction<ISubmitRemoveUserFromEnvPayload>,
    ) {},
    getUserList(state, action: PayloadAction<IGetUserListPayload>) {
      state.userList.loading = true;
    },
    setUserList(state, action: PayloadAction<IUserListItem[]>) {
      state.userList.list = action.payload;
    },
    setUserListLoading(state, action: PayloadAction<boolean>) {
      state.userList.loading = action.payload;
    },

    getUserDetails(state, action: PayloadAction<IGetUserDetailsPayload>) {
      state.userDetails.loading = true;
    },
    setUserDetails(state, action: PayloadAction<IUserDetails | null>) {
      state.userDetails.user = action.payload;
    },
    setUserDetailsLoading(state, action: PayloadAction<boolean>) {
      state.userDetails.loading = action.payload;
    },

    setUserDetailsCompanies(
      state,
      action: PayloadAction<IUserCompaniesListItem[]>,
    ) {
      state.userDetails.list = action.payload;
    },

    toggleAddUserForm(
      state,
      action: PayloadAction<{
        isOpen: boolean;
        type?: 'add' | 'edit';
        userEmail?: string;
        permissions?: IAccessRightsNumbers;
      }>,
    ) {
      state.addUserForm.isOpen = action.payload.isOpen;
      if (action.payload.type) {
        state.addUserForm.type = action.payload.type;
      }
      if (action.payload.userEmail) {
        state.addUserForm.userEmail = action.payload.userEmail;
      }
      if (action.payload.permissions) {
        state.addUserForm.permissions = action.payload.permissions;
      }
    },
    toggleAddUserCompanyForm(
      state,
      action: PayloadAction<{
        isOpen: boolean;
        type?: 'add' | 'edit';
        email?: string;
        permissions?: IAccessRightsNumbers;
        companyId?: number;
        companyName?: string;
      }>,
    ) {
      state.addUserCompanyForm.isOpen = action.payload.isOpen;
      state.addUserCompanyForm.permissions = action.payload.permissions;
      state.addUserCompanyForm.companyId = action.payload.companyId;
      state.addUserCompanyForm.companyName = action.payload.companyName;

      if (action.payload.type) {
        state.addUserCompanyForm.type = action.payload.type;
      }
    },
    setAddUserCompanyFormLoading(state, action: PayloadAction<boolean>) {
      state.addUserCompanyForm.loading = action.payload;
    },
    setAddUserFormLoading(state, action: PayloadAction<boolean>) {
      state.addUserForm.loading = action.payload;
    },
    submitAddUserForm(
      state,
      action: PayloadAction<ISubmitAddUserFormPayload>,
    ) {},

    submitAddUserToCompany(
      state,
      action: PayloadAction<ISubmitAddUserToCompanyPayload>,
    ) {},
  },
});

export const { actions: userActions } = slice;

export const useUserSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectSaga({ key: slice.name, saga: userSaga });
  return { actions: slice.actions };
};

/**
 * Example Usage:
 *
 * export function MyComponentNeedingThisSlice() {
 *  const { actions } = useUserSlice();
 *
 *  const onButtonClick = (evt) => {
 *    dispatch(actions.someAction());
 *   };
 * }
 */
